import React, { useRef, lazy } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
const EmailEditor = lazy(() => import('react-email-editor'));

const CreateTemplateModal = ({
    isOpen,
    onClose,
    newTemplate,
    setNewTemplate,
    scenarios,
    onCreateTemplate,
}) => {
    const emailEditorRef = useRef(null);

    const handleCreateClick = () => {
        emailEditorRef.current.editor.exportHtml(async (data) => {
            const { design, html } = data;

            // 1. Найти изображения с Unlayer CDN
            const extractImageUrls = (html) => {
                const urls = [];
                const regex = /<img[^>]*src="(https:\/\/assets\.unlayer\.com\/[^\"]+)"[^>]*>/g;
                let match;
                while ((match = regex.exec(html)) !== null) {
                    urls.push(match[1]);
                }
                return urls;
            };

            const urls = extractImageUrls(html);
            console.log('🖼️ Найдено изображений:', urls);

            try {
                // 2. Отправить ссылки на бэкенд для загрузки
                const responses = await Promise.all(
                    urls.map((url) =>
                        api.post('/api/uploads/from-url/', { url })
                            .then((res) => res.data)
                            .catch((err) => {
                                console.error(`Ошибка при загрузке ${url}:`, err);
                                return null;
                            })
                    )
                );

                // 3. Заменить старые ссылки на новые
                let updatedHtml = html;
                urls.forEach((originalUrl, index) => {
                    const newUrl = responses[index]?.url;
                    if (newUrl) {
                        updatedHtml = updatedHtml.replaceAll(originalUrl, newUrl);
                    }
                });

                // 🔧 4. Собрать финальный шаблон (с step_number!)
                const templateToCreate = {
                    title: newTemplate.name,
                    scenario: newTemplate.scenario,
                    subject: newTemplate.subject,
                    tone: newTemplate.tone,
                    step_number: newTemplate.step_number ?? 1,
                    content: JSON.stringify(design),
                    html: updatedHtml,
                };

                // 🚀 5. Отправить готовый шаблон
                onCreateTemplate(templateToCreate);

            } catch (err) {
                console.error('❌ Ошибка при обработке шаблона:', err);
            }
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-full !max-w-[1400px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Create New Template</DialogTitle>
                    <DialogDescription>Define a new email template for your campaigns.</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className='mb-3'>Template Name</Label>
                        <Input
                            placeholder="e.g., New Field Introduction"
                            value={newTemplate.name ?? ''}
                            onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </div>

                    <div className="grid grid-cols-3">
                        <div>
                            <Label className='mb-3'>Scenario</Label>
                            <Select value={newTemplate.scenario || ''} onValueChange={(value) => setNewTemplate(prev => ({ ...prev, scenario: value }))}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a scenario" />
                                </SelectTrigger>
                                <SelectContent>
                                    {scenarios.map((s, i) => (
                                        <SelectItem key={i} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='ml-3'>
                            <Label className='mb-3'>Tone</Label>
                            <Select value={newTemplate.tone || ''} onValueChange={(value) => setNewTemplate(prev => ({ ...prev, tone: value }))}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a tone" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Professional">Professional</SelectItem>
                                    <SelectItem value="Casual">Casual</SelectItem>
                                    <SelectItem value="Urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className='mb-3'>Email Step Number</Label>
                            <Input
                                type="number"
                                min="1"
                                placeholder="e.g., 1"
                                value={newTemplate.step_number ?? 1}
                                onChange={(e) => setNewTemplate(prev => ({ ...prev, step_number: Number(e.target.value) }))}
                            />
                        </div>
                    </div>

                    <div>
                        <Label className='mb-3'>Subject</Label>
                        <Input
                            placeholder="e.g., Transform Your Athletic Program"
                            value={newTemplate.subject ?? ''}
                            onChange={(e) => setNewTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        />
                    </div>

                    <div>
                        <Label className='mb-3'>Email Body</Label>
                        <div className="rounded border overflow-hidden" style={{ minHeight: 300 }}>
                            <EmailEditor
                                ref={emailEditorRef}
                                onReady={() => {
                                    const editor = emailEditorRef.current?.editor;
                                    if (!editor) return;

                                    editor.registerCallback('image', async (file, done) => {
                                        console.log('📦 Intercepted image upload:', file);
                                        const uploadedFile = file.attachments?.[0];
                                        if (!uploadedFile) {
                                            console.warn('⚠️ No file to upload');
                                            return done({ progress: 100, url: '' });
                                        }

                                        const formData = new FormData();
                                        formData.append('image', uploadedFile);

                                        try {
                                            const res = await api.post('/api/uploads/image/', formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' },
                                            });

                                            const data = res.data;
                                            console.log('✅ Image uploaded to backend:', data);
                                            done({ progress: 100, url: data.url || '' });
                                        } catch (err) {
                                            console.error('❌ Upload failed:', err);
                                            done({ progress: 100, url: '' });
                                        }
                                    });
                                }}
                                options={{
                                    appearance: {
                                        theme: 'light',
                                    },
                                    features: {
                                        imageEditor: false,
                                        stockImages: false,
                                    },
                                    tools: {
                                        image: {
                                            enabled: true,
                                            position: 1,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleCreateClick} disabled={!newTemplate.name || !newTemplate.scenario || !newTemplate.subject || !newTemplate.tone}>Create Template</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CreateTemplateModal;